import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import React from "react";

const ChartSkeleton: React.FC = () => {
  return (
    <div className="relative h-full w-full overflow-hidden rounded-md border border-gray-100 bg-white/50 p-4 shadow-sm transition-all">
      {/* Loading indicator text */}
      <div className="absolute left-1/2 top-1 z-10 -translate-x-1/2 transform">
        <div className="rounded-full bg-gray-100/80 px-3 py-1 text-xs font-medium text-gray-500 shadow-sm backdrop-blur-sm">
          <span className="inline-block animate-pulse">Loading chart data</span>
          <span className="ml-1 inline-block animate-bounce delay-100">.</span>
          <span className="inline-block animate-bounce delay-200">.</span>
          <span className="inline-block animate-bounce delay-300">.</span>
        </div>
      </div>
      {/* Chart background with subtle pulse */}
      <div
        className="absolute inset-0 animate-pulse bg-gradient-to-b from-transparent to-gray-50/30 backdrop-blur-[1px]"
        style={{ animationDuration: "3s" }}
      ></div>
      {/* Y-axis line */}
      <div className="absolute bottom-10 left-14 top-8 w-[1px] bg-gray-100/80"></div>
      {/* X-axis line */}
      <div className="absolute bottom-10 left-14 right-6 h-[1px] bg-gray-100/80"></div>
      {/* Y-axis title */}
      <div className="absolute left-2 top-1/2 -translate-y-1/2 -rotate-90 transform whitespace-nowrap">
        <Skeleton width={40} height={12} className="rounded-md opacity-40" />
      </div>
      {/* Y-axis tick marks and labels with staggered animation */}
      <div className="absolute bottom-10 left-0 top-8 flex flex-col justify-between">
        {Array(5)
          .fill(0)
          .map((_, i) => (
            <div key={i} className="flex items-center">
              <div className="h-[1px] w-3 bg-gray-100/80"></div>
              <div className="ml-1">
                <Skeleton
                  width={20}
                  height={8}
                  className={`animate-pulse rounded-sm opacity-40`}
                  style={{
                    animationDelay: `${i * 0.15}s`,
                    animationDuration: "1.5s",
                  }}
                />
              </div>
            </div>
          ))}
      </div>
      {/* X-axis labels with staggered animation */}
      <div className="absolute bottom-0 left-14 right-6 flex justify-between">
        {Array(6)
          .fill(0)
          .map((_, i) => (
            <div key={i} className="flex flex-col items-center">
              <div className="h-3 w-[1px] bg-gray-100/80"></div>
              <div className="mt-1">
                <Skeleton
                  width={30}
                  height={8}
                  className="animate-pulse rounded-sm opacity-40"
                  style={{
                    animationDelay: `${i * 0.15}s`,
                    animationDuration: "1.5s",
                  }}
                />
              </div>
            </div>
          ))}
      </div>
      {/* Legend at top with shimmer effect */}
      <div className="absolute right-4 top-1 flex items-center justify-end space-x-6 rounded-md bg-white/30 p-1 shadow-sm backdrop-blur-[1px]">
        {Array(2)
          .fill(0)
          .map((_, i) => (
            <div key={i} className="flex items-center">
              <Skeleton
                width={10}
                height={10}
                className={cn(
                  "mr-2 animate-pulse rounded-full shadow-sm",
                  i === 0 ? "bg-blue-200/80" : "bg-purple-200/80",
                )}
                style={{ animationDuration: "2s" }}
              />
              <Skeleton
                width={60}
                height={10}
                className="animate-pulse rounded-sm opacity-60"
                style={{
                  animationDelay: `${i * 0.2}s`,
                  animationDuration: "2s",
                }}
              />
            </div>
          ))}
      </div>
      {/* Grid lines with subtle fade-in/out */}
      {Array(4)
        .fill(0)
        .map((_, i) => (
          <div
            key={i}
            className="absolute left-14 right-6 h-[1px] animate-pulse bg-gray-100/60"
            style={{
              top: `${24 + i * ((100 - 24 - 16) / 4)}%`,
              animationDuration: "3s",
              animationDelay: `${i * 0.25}s`,
            }}
          ></div>
        ))}
      {/* Chart lines with gradient shimmer */}
      <div className="absolute bottom-10 left-14 right-6 top-10 overflow-hidden">
        {/* First dataset line - subtle glow effect */}
        <div className="relative h-full">
          <svg className="h-full w-full drop-shadow-sm">
            <path
              d="M0,80 C50,60 100,90 150,50 C200,30 250,70 300,40 C350,60 400,20 450,50"
              fill="none"
              stroke="url(#blueGradient)"
              strokeWidth="2.5"
              strokeDasharray="5,3"
              className="opacity-70"
            >
              <animate
                attributeName="stroke-dashoffset"
                values="12;0"
                dur="1.5s"
                repeatCount="indefinite"
              />
            </path>
            <linearGradient id="blueGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(59, 130, 246, 0.4)" />
              <stop offset="50%" stopColor="rgba(59, 130, 246, 0.7)" />
              <stop offset="100%" stopColor="rgba(59, 130, 246, 0.4)" />
              <animateTransform
                attributeName="gradientTransform"
                type="translate"
                from="-1"
                to="1"
                dur="1.5s"
                repeatCount="indefinite"
              />
            </linearGradient>
          </svg>
        </div>

        {/* Second dataset line with animated dash offset */}
        <div className="relative h-full" style={{ marginTop: "-100%" }}>
          <svg className="h-full w-full drop-shadow-sm">
            <path
              d="M0,50 C50,80 100,30 150,60 C200,40 250,90 300,60 C350,30 400,50 450,20"
              fill="none"
              stroke="url(#purpleGradient)"
              strokeWidth="2.5"
              strokeDasharray="5,3"
              className="opacity-70"
            >
              <animate
                attributeName="stroke-dashoffset"
                values="12;0"
                dur="1.5s"
                repeatCount="indefinite"
              />
            </path>
            <linearGradient
              id="purpleGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="rgba(147, 51, 234, 0.4)" />
              <stop offset="50%" stopColor="rgba(147, 51, 234, 0.7)" />
              <stop offset="100%" stopColor="rgba(147, 51, 234, 0.4)" />
              <animateTransform
                attributeName="gradientTransform"
                type="translate"
                from="-1"
                to="1"
                dur="1.5s"
                repeatCount="indefinite"
              />
            </linearGradient>
          </svg>
        </div>
      </div>
      {/* Data points for first dataset with enhanced animation */}
      <div className="absolute bottom-10 left-14 right-6 top-10">
        {[
          { left: "10%", top: "60%" },
          { left: "30%", top: "70%" },
          { left: "50%", top: "30%" },
          { left: "70%", top: "50%" },
          { left: "90%", top: "40%" },
        ].map((pos, i) => (
          <div
            key={`point1-${i}`}
            className="absolute h-3 w-3 animate-pulse rounded-full border-2 border-blue-300/80 bg-white shadow-md"
            style={{
              left: pos.left,
              top: pos.top,
              transform: "translate(-50%, -50%)",
              animationDuration: "2s",
              animationDelay: `${i * 0.2}s`,
            }}
          >
            <div
              className="absolute inset-0 animate-ping rounded-full bg-blue-100 opacity-75"
              style={{ animationDuration: "3s", animationDelay: `${i * 0.2}s` }}
            ></div>
          </div>
        ))}
      </div>
      {/* Data points for second dataset with enhanced animation */}
      <div className="absolute bottom-10 left-14 right-6 top-10">
        {[
          { left: "10%", top: "40%" },
          { left: "30%", top: "20%" },
          { left: "50%", top: "60%" },
          { left: "70%", top: "30%" },
          { left: "90%", top: "10%" },
        ].map((pos, i) => (
          <div
            key={`point2-${i}`}
            className="absolute h-3 w-3 animate-pulse rounded-full border-2 border-purple-300/80 bg-white shadow-md"
            style={{
              left: pos.left,
              top: pos.top,
              transform: "translate(-50%, -50%)",
              animationDuration: "2s",
              animationDelay: `${i * 0.2 + 0.1}s`,
            }}
          >
            <div
              className="absolute inset-0 animate-ping rounded-full bg-purple-100 opacity-75"
              style={{
                animationDuration: "3s",
                animationDelay: `${i * 0.2 + 0.1}s`,
              }}
            ></div>
          </div>
        ))}
      </div>
      {/* Enhanced shimmer overlay effect */}
      <div
        className="animate-shimmer absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent"
        style={{ backgroundSize: "200% 100%", animationDuration: "1.8s" }}
      ></div>
      {/* Loading corner indicator */}
      <div className="absolute bottom-2 right-2 flex h-6 w-6 items-center justify-center">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-200 border-t-blue-500"></div>
      </div>
    </div>
  );
};

export default ChartSkeleton;
